import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ChevronDown,
  ChevronUp,
  Search,
  MapPin,
  Pin,
  ArrowLeft,
  Building2,
  Loader2,
  Check,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAppServices } from "hook/services";
import { useUserInfo } from "context/user";
import { useSubAccountInfo } from "context/sub_account";

interface Account {
  _id: string;
  name: string;
  city?: string;
  zip?: string;
}

interface LocationSwitcherProps {}

const LocationSwitcher: React.FC<LocationSwitcherProps> = ({}) => {
  const navigate = useNavigate();
  const AppService = useAppServices();
  const { user } = useUserInfo();
  const { locationId } = useParams();
  const { subaccount, Update } = useSubAccountInfo();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [position, setPosition] = useState<{
    top: number;
    left: number;
    width: number;
  }>({
    top: 0,
    left: 0,
    width: 0,
  });

  // Pagination states
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0,
    hasMore: false,
  });

  const triggerRef = useRef<HTMLDivElement | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout>();

  const buildQueryString = () => {
    const params = new URLSearchParams();

    // Required company filter
    if (user?.companyId) {
      params.append("companyId", user.companyId);
    }

    // Pagination parameters
    params.append("page", pagination.page.toString());
    params.append("limit", pagination.limit.toString());

    // Search parameter
    if (search) {
      params.append("search", search);
    }

    return params.toString();
  };

  const getSubAccounts = async (isSearch: boolean = false) => {
    if (isSearch) {
      setSearchLoading(true);
    } else {
      setLoading(true);
    }

    try {
      const query = buildQueryString();
      const { response, error } = await AppService.sub_account.filter({
        query: query,
      });

      if (response && response.data) {
        if (isSearch || pagination.page === 1) {
          // Replace results for new searches or first page
          setAccounts(response.data);
        } else {
          // Append results for pagination
          setAccounts((prev) => [...prev, ...response.data]);
        }

        // Update pagination info from API response
        if (response.pagination) {
          setPagination((prev) => ({
            ...prev,
            total: response.pagination.total,
            pages: response.pagination.pages,
            hasMore: response.pagination.page < response.pagination.pages,
          }));
        }
      } else {
        setAccounts([]);
        setPagination((prev) => ({
          ...prev,
          total: 0,
          pages: 0,
          hasMore: false,
        }));
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
      setAccounts([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  let pageLoad = true;
  useEffect(() => {
    if (isOpen) {
      if (pageLoad) {
        // Reset to first page when opening dropdown
        setPagination((prev) => ({ ...prev, page: 1 }));
        getSubAccounts();
        pageLoad = false;
      }
    }
  }, [isOpen]);

  // Debounced search
  useEffect(() => {
    if (!isOpen) return;

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for search
    searchTimeoutRef.current = setTimeout(() => {
      if (search.trim() !== "") {
        setPagination((prev) => ({ ...prev, page: 1 }));
        getSubAccounts(true);
      } else {
        // If search is empty, reload initial data
        setPagination((prev) => ({ ...prev, page: 1 }));
        getSubAccounts();
      }
    }, 500); // 500ms debounce

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [search]);

  // Load more locations
  const loadMore = () => {
    if (pagination.hasMore && !loading) {
      setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
    }
  };

  // Load more when pagination page changes
  useEffect(() => {
    if (pagination.page > 1 && isOpen) {
      getSubAccounts();
    }
  }, [pagination.page]);

  const handleSwitch = (type: string, acc?: Account) => {
    if (type === "agency") {
      navigate(`/agency/dashboard`);
    } else {
      // Update(acc);
      navigate(`/location/${acc?._id}/dashboard`);
    }
    setIsOpen(false);
    // Reset search and pagination when closing
    setSearch("");
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const toggleDropdown = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosition({
        top: rect.top,
        left: rect.right + 8,
        width: Math.max(rect.width, 380),
      });
    }
    setIsOpen((prev) => !prev);

    // Reset search and pagination when closing
    if (isOpen) {
      setSearch("");
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  };

  // Handle click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !dropdownRef.current?.contains(target)
      ) {
        setIsOpen(false);
        // Reset search and pagination when closing
        setSearch("");
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((word) => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const isCurrentLocation = (account: Account) => {
    return subaccount?._id === account._id;
  };

  // Handle scroll for infinite loading
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const element = e.currentTarget;
    const isAtBottom =
      element.scrollHeight - element.scrollTop === element.clientHeight;

    if (isAtBottom && pagination.hasMore && !loading) {
      loadMore();
    }
  };

  return (
    <div className="relative" ref={triggerRef}>
      {/* Trigger Button */}
      <div className="flex justify-center mb-5">
        <button
          onClick={toggleDropdown}
          className="w-[95%] flex items-center justify-between bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg px-3 py-2.5 shadow-sm hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div
              className={`w-8 h-8 rounded-lg flex items-center justify-center font-semibold text-sm flex-shrink-0 ${
                locationId
                  ? "bg-gradient-to-br from-blue-500 to-blue-600 text-white"
                  : "bg-gradient-to-br from-gray-500 to-gray-600 text-white"
              }`}
            >
              <Building2 size={14} />
            </div>
            <div className="flex flex-col items-start min-w-0 flex-1">
              <span className="text-sm font-semibold text-gray-900 dark:text-white truncate w-full text-left">
                {locationId ? subaccount?.name || "Select Location" : "Agency"}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400 truncate w-full text-left">
                {locationId ? "Current Location" : "Agency Dashboard"}
              </span>
            </div>
          </div>
          {loading ? (
            <Loader2
              size={14}
              className="animate-spin text-gray-400 flex-shrink-0 ml-1"
            />
          ) : isOpen ? (
            <ChevronUp
              size={14}
              className="text-gray-500 dark:text-gray-400 flex-shrink-0 ml-1"
            />
          ) : (
            <ChevronDown
              size={14}
              className="text-gray-500 dark:text-gray-400 flex-shrink-0 ml-1"
            />
          )}
        </button>
      </div>

      {/* Dropdown + Backdrop */}
      {isOpen &&
        createPortal(
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/10 dark:bg-black/30 z-[9998]"
              onClick={() => setIsOpen(false)}
            />

            {/* Dropdown */}
            <div
              ref={dropdownRef}
              className="fixed z-[9999] bg-white dark:bg-gray-800 shadow-xl rounded-lg border border-gray-200 dark:border-gray-600 animate-dropdown-fade-in"
              style={{
                top: Math.min(position.top, window.innerHeight - 420),
                left: position.left,
                width: 410,
                maxHeight: "min(420px, 80vh)",
              }}
            >
              {/* Header */}
              <div className="p-4 border-b border-gray-100 dark:border-gray-700">
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-3">
                  Switch Location
                </h3>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search locations by name..."
                    className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    autoFocus
                  />
                  {searchLoading && (
                    <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-400" />
                  )}
                </div>
              </div>

              {/* Agency Switch */}
              {locationId && (
                <div className="border-b border-gray-100 dark:border-gray-700">
                  <button
                    onClick={() => handleSwitch("agency")}
                    className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors duration-150 group"
                  >
                    <div className="w-8 h-8 bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-gray-200 dark:group-hover:bg-gray-500 transition-colors">
                      <ArrowLeft size={16} />
                    </div>
                    <div className="flex flex-col items-start text-left">
                      <span className="font-semibold text-gray-900 dark:text-white">
                        Agency View
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        Switch back to agency dashboard
                      </span>
                    </div>
                  </button>
                </div>
              )}

              {/* Available Locations Section */}
              <div
                className="max-h-[280px] overflow-y-auto bg-white dark:bg-gray-800"
                onScroll={handleScroll}
              >
                <div className="px-4 py-2 sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-100 dark:border-gray-700 z-10">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    Available Locations{" "}
                    {pagination.total > 0 && `(${pagination.total})`}
                  </p>
                </div>

                {loading && pagination.page === 1 ? (
                  <div className="flex flex-col items-center justify-center py-8">
                    <Loader2
                      size={20}
                      className="animate-spin text-blue-500 mb-2"
                    />
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      Loading locations...
                    </p>
                  </div>
                ) : accounts.length > 0 ? (
                  <>
                    {accounts.map((acc) => (
                      <button
                        key={acc._id}
                        onClick={() => handleSwitch("location", acc)}
                        className={`w-full flex items-center justify-between px-4 py-3 transition-all duration-150 border-b border-gray-50 dark:border-gray-700 last:border-b-0 group
                          ${
                            isCurrentLocation(acc)
                              ? "bg-blue-50 dark:bg-blue-900/20 border-l-2 border-l-blue-500"
                              : "hover:bg-gray-50 dark:hover:bg-gray-700"
                          }`}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-white text-sm flex-shrink-0
                            ${
                              isCurrentLocation(acc)
                                ? "bg-gradient-to-br from-blue-500 to-blue-600"
                                : "bg-gradient-to-br from-gray-500 to-gray-600 group-hover:from-blue-500 group-hover:to-blue-600"
                            }`}
                          >
                            {getInitials(acc.name)}
                          </div>
                          <div className="flex flex-col items-start min-w-0 flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <span
                                className={`text-sm font-semibold truncate
                                ${
                                  isCurrentLocation(acc)
                                    ? "text-blue-900 dark:text-blue-100"
                                    : "text-gray-900 dark:text-white"
                                }`}
                              >
                                {acc.name}
                              </span>
                              {isCurrentLocation(acc) && (
                                <div className="flex items-center gap-1 px-1.5 py-0.5 bg-blue-100 dark:bg-blue-800 text-blue-700 dark:text-blue-200 rounded text-xs font-medium">
                                  <Check size={10} />
                                  Current
                                </div>
                              )}
                            </div>
                            {(acc.city || acc.zip) && (
                              <span className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1 truncate w-full">
                                <MapPin className="w-3 h-3 flex-shrink-0" />
                                {[acc.city, acc.zip].filter(Boolean).join(", ")}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* <Pin
                          size={14}
                          className={`flex-shrink-0 transition-colors ${
                            isCurrentLocation(acc)
                              ? "text-blue-500"
                              : "text-gray-300 dark:text-gray-500 group-hover:text-gray-400 dark:group-hover:text-gray-300"
                          }`}
                        /> */}
                      </button>
                    ))}

                    {/* Load More Indicator */}
                    {pagination.hasMore && (
                      <div className="px-4 py-3 border-b border-gray-50 dark:border-gray-700">
                        <button
                          onClick={loadMore}
                          disabled={loading}
                          className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm text-gray-600 dark:text-gray-300 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                        >
                          {loading ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              Loading more...
                            </>
                          ) : (
                            "Load More Locations"
                          )}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
                    <div className="w-12 h-12 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-3">
                      <Search size={18} className="text-gray-400" />
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                      No locations found
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 max-w-[240px]">
                      {search
                        ? `No locations matching "${search}"`
                        : "No locations available for your account"}
                    </p>
                    {search && (
                      <button
                        onClick={() => setSearch("")}
                        className="mt-2 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                  {accounts.length} of {pagination.total} location
                  {pagination.total !== 1 ? "s" : ""} shown
                  {pagination.hasMore && " • Scroll to load more"}
                </p>
              </div>
            </div>
          </>,
          document.body
        )}
    </div>
  );
};

// Inject animations
const style = document.createElement("style");
style.innerHTML = `
@keyframes dropdown-fade-in {
  from { 
    opacity: 0; 
    transform: translateX(-10px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateX(0) scale(1); 
  }
}

.animate-dropdown-fade-in {
  animation: dropdown-fade-in 0.2s ease-out forwards;
}

/* Custom scrollbar */
.max-h-\\[280px\\]::-webkit-scrollbar {
  width: 4px;
}

.max-h-\\[280px\\]::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 2px;
}

.max-h-\\[280px\\]::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 2px;
}

.max-h-\\[280px\\]::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

/* Dark mode scrollbar */
@media (prefers-color-scheme: dark) {
  .max-h-\\[280px\\]::-webkit-scrollbar-track {
    background: #374151;
  }

  .max-h-\\[280px\\]::-webkit-scrollbar-thumb {
    background: #6b7280;
  }

  .max-h-\\[280px\\]::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
}

.dark .max-h-\\[280px\\]::-webkit-scrollbar-track {
  background: #374151;
}

.dark .max-h-\\[280px\\]::-webkit-scrollbar-thumb {
  background: #6b7280;
}

.dark .max-h-\\[280px\\]::-webkit-scrollbar-thumb:hover {
  background: #9ca3af;
}
`;
document.head.appendChild(style);

export default LocationSwitcher;
